import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { FootprintShapeType, PositionType } from 'data/types';
import { useFootprintShapeDrag } from 'hooks/use-footprint-shape-drag';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { placeFootprintAreaLabels } from 'utils/footprint-area-label-placement';
import { getFootprintOutlineEditorTooltipLines } from 'utils/footprint-cursor';
import { footprintShapePreviewTransform } from 'utils/footprint-shape-drag';
import {
  applyVertexScale,
  beginVertexScale,
  buildShapesRenderData,
  deleteOutlineVertex,
  FOOTPRINT_CANVAS_OFFSET,
  footprintPixelToCanvas,
  FootprintShapeDragState,
  FootprintShapeHoverTarget,
  getOutlineVertexCursor,
  insertShapeVertex,
  isSameHoverTarget,
  moveShapeEdgePerpendicular,
  moveShapeVertex,
  VertexScaleContext
} from 'utils/footprint-shapes-editing';
import FootprintAreaLabel from './footprint-area-label';
import FootprintCursorTooltip from './footprint-cursor-tooltip';
import FootprintShapeFill from './footprint-shape-fill';

type FootprintShapesLayerProps = Readonly<{
  shapes: FootprintShapeType[];
  onShapeChange: (
    shapeIndex: number,
    points: PositionType[],
    vertexEdited: boolean
  ) => void;
  onDeleteShape: (shapeIndex: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  zoom: number;
  showArea?: boolean;
  readOnly?: boolean;
  isLayoutImageSelected?: boolean;
  onDoubleClick?: () => void;
}>;

const VERTEX_RADIUS = 5;
const VERTEX_HOVER_RADIUS = 7;

export default function FootprintShapesLayer({
  shapes,
  onShapeChange,
  onDeleteShape,
  onDragStart,
  onDragEnd,
  zoom,
  showArea = true,
  readOnly = false,
  isLayoutImageSelected = false,
  onDoubleClick
}: FootprintShapesLayerProps) {
  const [dragState, setDragState] = useState<FootprintShapeDragState | null>(
    null
  );
  const [hoveredVertex, setHoveredVertex] =
    useState<FootprintShapeHoverTarget | null>(null);
  const [hoveredEdge, setHoveredEdge] =
    useState<FootprintShapeHoverTarget | null>(null);
  const [hoveredFillIndex, setHoveredFillIndex] = useState<number | null>(null);
  const [pointerScreen, setPointerScreen] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const dragAnchorRef = useRef<PositionType | null>(null);
  const vertexScaleCtxRef = useRef<VertexScaleContext | null>(null);
  const dragDidMoveRef = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const interactive = !readOnly && !isLayoutImageSelected;

  const clearHoverState = useCallback(() => {
    setHoveredVertex(null);
    setHoveredEdge(null);
    setHoveredFillIndex(null);
  }, []);

  const settleShapeDrag = useCallback(() => {
    clearHoverState();
    setPointerScreen(null);
    onDragEnd?.();
  }, [clearHoverState, onDragEnd]);

  const shapeDragPreview = useFootprintShapeDrag({
    shapes,
    zoom,
    onShapeChange,
    onDeleteShape,
    onDragStart,
    onDragEnd: settleShapeDrag
  });

  const screenToGrid = useCallback(
    (clientX: number, clientY: number): PositionType => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };

      const rect = svg.getBoundingClientRect();
      const x = (clientX - rect.left) / zoom;
      const y = (clientY - rect.top) / zoom;

      return {
        x: Math.round((x - FOOTPRINT_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE),
        y: Math.round((y - FOOTPRINT_CANVAS_OFFSET) / STRUCTURE_GRID_SIZE)
      };
    },
    [zoom]
  );

  useEffect(() => {
    if (!dragState) return undefined;
    const shape = shapes[dragState.shapeIndex];
    if (!shape) return undefined;
    const { points } = shape;
    const vertexEdited = shape.vertexEdited === true;
    const { shapeIndex } = dragState;

    const handleMouseMove = (e: MouseEvent) => {
      const gridPoint = screenToGrid(e.clientX, e.clientY);

      if (dragState.kind === 'vertex') {
        const current = points[dragState.vertexIndex];
        if (!current) return;
        if (gridPoint.x === current.x && gridPoint.y === current.y) return;
        dragDidMoveRef.current = true;
        onShapeChange(
          shapeIndex,
          moveShapeVertex(points, dragState.vertexIndex, gridPoint),
          vertexEdited
        );
        return;
      }

      if (dragState.kind === 'vertex-scale') {
        const ctx = vertexScaleCtxRef.current;
        if (!ctx) return;
        const scaled = applyVertexScale(ctx, gridPoint);
        if (JSON.stringify(scaled) === JSON.stringify(points)) return;
        dragDidMoveRef.current = true;
        onShapeChange(shapeIndex, scaled, vertexEdited);
        return;
      }

      const anchor = dragAnchorRef.current;
      if (!anchor) return;
      const dx = gridPoint.x - anchor.x;
      const dy = gridPoint.y - anchor.y;
      if (dx === 0 && dy === 0) return;

      dragAnchorRef.current = gridPoint;
      dragDidMoveRef.current = true;
      onShapeChange(
        shapeIndex,
        moveShapeEdgePerpendicular(points, dragState.edgeIndex, dx, dy),
        vertexEdited
      );
    };

    const handleMouseUp = (e: MouseEvent) => {
      const shouldInsertVertex =
        dragState.kind === 'edge' && !dragDidMoveRef.current;
      setDragState(null);
      dragAnchorRef.current = null;
      vertexScaleCtxRef.current = null;
      onDragEnd?.();
      if (!shouldInsertVertex) return;
      onShapeChange(
        shapeIndex,
        insertShapeVertex(
          points,
          dragState.edgeIndex,
          screenToGrid(e.clientX, e.clientY)
        ),
        true
      );
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    globalThis.addEventListener('mouseup', handleMouseUp);
    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
      globalThis.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, shapes, onShapeChange, screenToGrid, onDragEnd]);

  const handleVertexMouseDown = useCallback(
    (e: React.MouseEvent, shapeIndex: number, vertexIndex: number) => {
      if (!interactive) return;
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      const shape = shapes[shapeIndex];
      if (!shape || shape.points.length < 3) return;
      dragDidMoveRef.current = false;
      if (!shape.vertexEdited) {
        const ctx = beginVertexScale(shape.points, vertexIndex);
        if (ctx) {
          vertexScaleCtxRef.current = ctx;
          setDragState({ kind: 'vertex-scale', shapeIndex, vertexIndex });
          onDragStart?.();
          return;
        }
      }
      setDragState({ kind: 'vertex', shapeIndex, vertexIndex });
      onDragStart?.();
    },
    [interactive, shapes, onDragStart]
  );

  const deleteVertex = useCallback(
    (shapeIndex: number, vertexIndex: number) => {
      clearHoverState();
      const shape = shapes[shapeIndex];
      if (!shape) return;
      const next = deleteOutlineVertex(shape.points, vertexIndex);
      if (next.length < 3) {
        onDeleteShape(shapeIndex);
        return;
      }
      onShapeChange(shapeIndex, next, true);
    },
    [shapes, onShapeChange, onDeleteShape, clearHoverState]
  );

  const handleVertexClick = useCallback(
    (e: React.MouseEvent, shapeIndex: number, vertexIndex: number) => {
      if (!interactive) return;
      if (dragDidMoveRef.current) return;
      e.stopPropagation();
      deleteVertex(shapeIndex, vertexIndex);
    },
    [interactive, deleteVertex]
  );

  const handleVertexRightClick = useCallback(
    (e: React.MouseEvent, shapeIndex: number, vertexIndex: number) => {
      if (!interactive) return;
      e.preventDefault();
      e.stopPropagation();
      deleteVertex(shapeIndex, vertexIndex);
    },
    [interactive, deleteVertex]
  );

  const handleEdgeMouseDown = useCallback(
    (e: React.MouseEvent, shapeIndex: number, edgeIndex: number) => {
      if (!interactive) return;
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      dragAnchorRef.current = screenToGrid(e.clientX, e.clientY);
      dragDidMoveRef.current = false;
      setDragState({ kind: 'edge', shapeIndex, edgeIndex });
      onDragStart?.();
    },
    [interactive, screenToGrid, onDragStart]
  );

  const renderData = useMemo(() => buildShapesRenderData(shapes), [shapes]);
  const areaLabelPlacements = useMemo(
    () =>
      showArea
        ? placeFootprintAreaLabels(shapes.map((shape) => shape.points))
        : [],
    [shapes, showArea]
  );

  const isDragging = dragState !== null || shapeDragPreview !== null;
  const hoveredShapeIndex =
    hoveredVertex?.shapeIndex ?? hoveredEdge?.shapeIndex ?? hoveredFillIndex;
  const hoveredShape =
    hoveredShapeIndex === null ? undefined : shapes[hoveredShapeIndex];

  const tooltipLines = getFootprintOutlineEditorTooltipLines({
    interactive,
    isDragging,
    hoveredVertexIndex: hoveredVertex?.index ?? null,
    hoveredEdgeIndex: hoveredEdge?.index ?? null,
    isHoveringFill: hoveredFillIndex !== null,
    pointsLength: hoveredShape?.points.length ?? 0,
    hasCursorPosition: pointerScreen !== null,
    vertexEdited: hoveredShape?.vertexEdited === true
  });

  const trackPointer = (e: React.MouseEvent) =>
    setPointerScreen({ x: e.clientX, y: e.clientY });

  return (
    <svg
      ref={svgRef}
      className='absolute z-[25]'
      style={{
        left: `-${FOOTPRINT_CANVAS_OFFSET}px`,
        top: `-${FOOTPRINT_CANVAS_OFFSET}px`,
        width: `${FOOTPRINT_CANVAS_OFFSET * 2}px`,
        height: `${FOOTPRINT_CANVAS_OFFSET * 2}px`,
        overflow: 'visible',
        pointerEvents: 'none'
      }}
    >
      {renderData.map((data, shapeIndex) => {
        if (!data) return null;
        const shape = shapes[shapeIndex];
        const vertexEdited = shape.vertexEdited === true;
        const areaLabelPlacement = areaLabelPlacements[shapeIndex];
        const previewTransform = footprintShapePreviewTransform(
          shapeDragPreview?.shapeIndex === shapeIndex
            ? shapeDragPreview.delta
            : null
        );
        return (
          <g
            // eslint-disable-next-line react/no-array-index-key
            key={`footprint-shape-${shapeIndex}`}
            transform={previewTransform}
          >
            <FootprintShapeFill
              shapeIndex={shapeIndex}
              pathD={data.pathD}
              interactive={interactive}
              readOnly={readOnly}
              highlighted={hoveredFillIndex === shapeIndex}
              onDoubleClick={onDoubleClick}
              onMouseEnter={() => setHoveredFillIndex(shapeIndex)}
              onMouseLeave={() => setHoveredFillIndex(null)}
              onMouseMove={trackPointer}
            />

            <path
              d={data.pathD}
              fill='none'
              stroke='#000000'
              strokeWidth={4}
              strokeLinecap='round'
              strokeLinejoin='round'
              style={{ pointerEvents: 'none' }}
            />

            {interactive &&
              data.edges.map((edge) => (
                <line
                  key={`edge-hit-${edge.index}`}
                  x1={edge.x1}
                  y1={edge.y1}
                  x2={edge.x2}
                  y2={edge.y2}
                  stroke='transparent'
                  strokeWidth={12}
                  style={{
                    cursor: edge.isHorizontal ? 'ns-resize' : 'ew-resize',
                    pointerEvents: 'stroke'
                  }}
                  onMouseDown={(e) =>
                    handleEdgeMouseDown(e, shapeIndex, edge.index)
                  }
                  onMouseEnter={() => {
                    setHoveredEdge({ shapeIndex, index: edge.index });
                    setHoveredFillIndex(shapeIndex);
                  }}
                  onMouseLeave={() => {
                    setHoveredEdge(null);
                    setHoveredFillIndex(null);
                  }}
                  onMouseMove={trackPointer}
                />
              ))}

            {interactive &&
              data.pixelPoints.map((point, vertexIndex) => {
                const isHovered = isSameHoverTarget(
                  hoveredVertex,
                  shapeIndex,
                  vertexIndex
                );
                const isDraggingThis =
                  (dragState?.kind === 'vertex' ||
                    dragState?.kind === 'vertex-scale') &&
                  dragState.shapeIndex === shapeIndex &&
                  dragState.vertexIndex === vertexIndex;
                return (
                  <circle
                    // eslint-disable-next-line react/no-array-index-key
                    key={`vertex-${vertexIndex}`}
                    cx={point.x}
                    cy={point.y}
                    r={isHovered ? VERTEX_HOVER_RADIUS : VERTEX_RADIUS}
                    fill='#3b82f6'
                    stroke='#ffffff'
                    strokeWidth={2}
                    style={{
                      cursor: getOutlineVertexCursor(
                        vertexEdited,
                        isDraggingThis,
                        data.points,
                        vertexIndex
                      ),
                      pointerEvents: 'auto',
                      transition: 'r 0.1s ease'
                    }}
                    onMouseDown={(e) =>
                      handleVertexMouseDown(e, shapeIndex, vertexIndex)
                    }
                    onClick={(e) =>
                      handleVertexClick(e, shapeIndex, vertexIndex)
                    }
                    onContextMenu={(e) =>
                      handleVertexRightClick(e, shapeIndex, vertexIndex)
                    }
                    onMouseEnter={() => {
                      setHoveredVertex({ shapeIndex, index: vertexIndex });
                      setHoveredFillIndex(shapeIndex);
                    }}
                    onMouseLeave={() => {
                      setHoveredVertex(null);
                      setHoveredFillIndex(null);
                    }}
                    onMouseMove={trackPointer}
                  />
                );
              })}

            {areaLabelPlacement && (
              <FootprintAreaLabel
                anchor={footprintPixelToCanvas(areaLabelPlacement.anchor)}
                areaLabel={areaLabelPlacement.areaLabel}
                dimensionsLabel={areaLabelPlacement.dimensionsLabel}
              />
            )}
          </g>
        );
      })}

      {pointerScreen && !readOnly && (
        <FootprintCursorTooltip
          lines={tooltipLines}
          screenX={pointerScreen.x}
          screenY={pointerScreen.y}
        />
      )}
    </svg>
  );
}
